import { DashboardRange } from '../types';

// Range of a dashboard card in the url of the list it opens: /users?created=week.
type RangeParam = 'today' | 'yesterday' | 'week' | 'month' | 'all';

const RANGE_PARAMS: { [range in DashboardRange]: RangeParam } = {
  [DashboardRange.TODAY]: 'today',
  [DashboardRange.YESTERDAY]: 'yesterday',
  [DashboardRange.WEEK]: 'week',
  [DashboardRange.MONTH]: 'month',
  [DashboardRange.ALL]: 'all',
};

// Paris days of the range, today included (as the dashboard figures). Yesterday starts 2 days back
// and stops at the end of its own day (getRangeTo).
const RANGE_DAYS: { [param: string]: number } = {
  today: 1,
  yesterday: 2,
  week: 7,
  month: 30,
};

const parisDate = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Paris',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

const getRangeParam = (range: DashboardRange) => RANGE_PARAMS[range];

// null for a missing or unknown value: the list is not filtered.
const parseRangeParam = (value: string | null): RangeParam | null => {
  if (value === 'today' || value === 'yesterday' || value === 'week' || value === 'month' || value === 'all') {
    return value;
  }
  return null;
};

// Noon UTC (13 h or 14 h in Paris) of the Paris day `daysBack` days before today.
const getParisNoon = (daysBack: number, now: number) => {
  const parts = {} as { [type: string]: number };
  parisDate.formatToParts(new Date(now)).forEach(({ type, value }) => {
    parts[type] = Number(value);
  });
  return Date.UTC(parts.year, parts.month - 1, parts.day - daysBack, 12);
};

// A time on the first Paris day of the range: the server starts the list at midnight in Paris of
// that day, as the dashboard. undefined for all (no lower bound).
const getRangeFrom = (param: RangeParam | null, now = Date.now()) => {
  const days = param ? RANGE_DAYS[param] : undefined;
  if (!days) {
    return undefined;
  }
  return getParisNoon(days - 1, now);
};

// A time on the last Paris day of the range: the server stops the list at the end of that day.
// Only yesterday has one, the other ranges go up to now.
const getRangeTo = (param: RangeParam | null, now = Date.now()) => {
  if (param !== 'yesterday') {
    return undefined;
  }
  return getParisNoon(1, now);
};

export type { RangeParam };
export { getRangeFrom, getRangeParam, getRangeTo, parseRangeParam };
